import * as React from 'react';
import { useCallback } from 'react';
import { inject, observer } from 'mobx-react';

import { Viewport, ViewLarge, ViewSmall } from 'bernie-view';
import { AnalyticsStore, ContextStore, RouteStore } from 'bernie-core';
import { UitkSelect } from 'uitk-react-select';
import { Level, liveAnnounce } from 'uitk-react-utils';

import { FlightsSortItem } from 'types/search/sort-filter';
import {
  AdvertisingStore,
  SearchCriteriaStore,
  SortFilterStore,
  ListingsStore,
  FlightsSelectedJourneyReviewStore
} from 'stores';
import { classNames } from 'uitk-react-utils';
import { trackAnalytics } from 'utility';
import { useHydrationEnable } from 'utility/hooks';

export interface SortProps {
  analytics?: AnalyticsStore;
  keySuffix: string;
  sortFilterStore?: SortFilterStore;
  searchCriteriaStore?: SearchCriteriaStore;
  routeStore?: RouteStore;
  advertisingStore?: AdvertisingStore;
  context?: ContextStore;
  classes?: string;
  hasVisibleLabel?: boolean;
  listingsStore?: ListingsStore;
  flightsSelectedJourneyReviewStore?: FlightsSelectedJourneyReviewStore;
}

const renderSortOptions = (options: FlightsSortItem[]) => {
  return options.map((option: FlightsSortItem) => {
    const { sortOrder, sortType } = option.sortType;
    const value = `${sortType}_${sortOrder}`;

    return (
      <option
        data-opt-id={value}
        data-test-id="sortDropdown-option"
        key={value}
        value={value}
        data-usage-msg={option.accessibility && option.accessibility.usage}
      >
        {option.label}
      </option>
    );
  });
};

export const Sort = inject(
  'analytics',
  'sortFilterStore',
  'searchCriteriaStore',
  'routeStore',
  'advertisingStore',
  'context',
  'listingsStore',
  'flightsSelectedJourneyReviewStore'
)(
  observer((props: SortProps) => {
    const hydrated = useHydrationEnable();

    const {
      analytics,
      sortFilterStore,
      searchCriteriaStore,
      routeStore,
      advertisingStore,
      context,
      keySuffix,
      classes,
      hasVisibleLabel,
      flightsSelectedJourneyReviewStore,
      listingsStore
    } = props;

    const sortPresentation = sortFilterStore && sortFilterStore.sortPresentation;
    const isNewSearch = !sortPresentation;
    const loading =
      (sortPresentation && sortPresentation.__typename === 'FlightsSortLoadingPresentation') || isNewSearch;

    const closeSideSheet = useCallback(() => {
      flightsSelectedJourneyReviewStore.detailsVisible = false;
      listingsStore.selectedOfferIndex = -1;
    }, [flightsSelectedJourneyReviewStore, listingsStore]);

    const updateSelectedSort = (currentSort: FlightsSortItem) => {
      const executeSearch = keySuffix !== 'onDrawer';
      sortPresentation.selectedOption = currentSort;

      if (executeSearch) {
        sortFilterStore.applySortAndFilter(searchCriteriaStore, routeStore, advertisingStore, context, analytics);
      }
    };

    const handleOnChange = event => {
      const { analytics } = props;
      const options =
        sortPresentation && sortPresentation.__typename === 'FlightsSortLoadedPresentation' && sortPresentation.options;
      const select = event.target;
      const { usageMsg } = select.options[select.selectedIndex].dataset;
      const currentOption = options && options[select.selectedIndex];

      trackAnalytics(currentOption.analytics, analytics);

      updateSelectedSort(currentOption);
      liveAnnounce(usageMsg, Level.POLITE);
    };

    const selectedOption = sortPresentation && sortPresentation.selectedOption;
    const label = sortPresentation && sortPresentation.label;
    const selectionActive = sortPresentation && sortPresentation.selectionActive;
    const options =
      sortPresentation && sortPresentation.__typename === 'FlightsSortLoadedPresentation' && sortPresentation.options;
    const sortOrder = selectedOption && selectedOption.sortType && selectedOption.sortType.sortOrder;
    const sortType = selectedOption && selectedOption.sortType && selectedOption.sortType.sortType;
    const disabled = !hydrated || !selectionActive || loading;
    const sortBarClassNames = classNames('l-b-padding-two', 'xl-b-padding-two', { [classes]: classes });

    return (
      (listingsStore.listings.length > 0 || !disabled) && (
        <section className={sortBarClassNames} data-test-id="sortBar">
          <fieldset>
            <Viewport>
              <ViewSmall>
                <>
                  <legend className="uitk-type-heading-500 all-b-margin-three" aria-hidden={true}>
                    {label}
                  </legend>
                  <h3 className="is-visually-hidden">{label}</h3>
                </>
              </ViewSmall>
              <ViewLarge />
            </Viewport>
            <UitkSelect
              data-test-id="sortDropdown"
              disabled={disabled}
              label={label}
              id="listings-sort"
              hasVisibleLabel={hasVisibleLabel}
              value={`${sortType}_${sortOrder}`}
              onChange={!loading ? handleOnChange : undefined}
              onClick={closeSideSheet}
              onKeyPress={closeSideSheet}
            >
              {loading ? renderSortOptions([selectedOption]) : options && renderSortOptions(options)}
            </UitkSelect>
          </fieldset>
          <Viewport>
            <ViewSmall>
              <hr className="all-y-margin-six" />
            </ViewSmall>
            <ViewLarge />
          </Viewport>
        </section>
      )
    );
  })
);
